// src/prototype/components/sep/SEPReportIndex.tsx
// ============================================================
// Sticky left rail navigation for SEPReportView
// - Smooth scroll aligned to app + page sticky headers
// - Scroll-position active section highlight
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const APP_HEADER_HEIGHT_PX = 72;
const STICKY_HEADER_BUFFER_PX = 12;
const EA_ACTIVATE_STICKY_HEADER_ID = 'ea-activate-sticky-header';
const STICKY_OFFSET_CSS_VAR = '--sep-sticky-offset';

export const SEP_SECTIONS = [
  { id: 'sep-exec-summary',          label: 'Executive Summary' },
  { id: 'sep-execution-strategy',    label: 'Execution Approach' },
  { id: 'sep-portfolio-impact',      label: 'Portfolio Financial Impact' },
  { id: 'sep-initiative-portfolio',  label: 'Initiative Portfolio' },
  { id: 'sep-exec-sequencing',       label: 'Execution Sequencing' },
  { id: 'sep-resource-plan',         label: 'Resource & Capacity Plan' },
  { id: 'sep-dependencies-risk',     label: 'Dependencies & Risk Summary' },
  { id: 'sep-governance',            label: 'Governance & Approval Framework' },
  { id: 'sep-impl-roadmap',          label: 'Implementation Roadmap' },
  { id: 'sep-recommendation',        label: 'Execution Recommendation' },
] as const;

type SectionId = typeof SEP_SECTIONS[number]['id'];

function measureStickyTopPx(): number {
  const eaHeader = document.getElementById(EA_ACTIVATE_STICKY_HEADER_ID);
  const eaHeaderHeight = eaHeader?.getBoundingClientRect().height ?? 0;
  return APP_HEADER_HEIGHT_PX + eaHeaderHeight + STICKY_HEADER_BUFFER_PX;
}

function applyStickyOffsetCssVar(offsetPx: number) {
  document.documentElement.style.setProperty(STICKY_OFFSET_CSS_VAR, `${offsetPx}px`);
}

export const SEPReportIndex: React.FC = () => {
  const [activeId, setActiveId] = useState<SectionId>(SEP_SECTIONS[0].id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [stickyTopPx, setStickyTopPx] = useState(
    APP_HEADER_HEIGHT_PX + 56 + STICKY_HEADER_BUFFER_PX,
  );

  const syncStickyOffset = useCallback(() => {
    const next = measureStickyTopPx();
    setStickyTopPx(next);
    applyStickyOffsetCssVar(next);
  }, []);

  useEffect(() => {
    syncStickyOffset();

    const eaHeader = document.getElementById(EA_ACTIVATE_STICKY_HEADER_ID);
    const resizeObserver = eaHeader ? new ResizeObserver(syncStickyOffset) : null;
    resizeObserver?.observe(eaHeader!);

    window.addEventListener('resize', syncStickyOffset);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', syncStickyOffset);
    };
  }, [syncStickyOffset]);

  const resolveActiveSection = useCallback((): SectionId => {
    const offset = measureStickyTopPx();
    const scrollPosition = window.scrollY + offset + 24;

    // Only snap to the last section when the user has scrolled down near the page bottom.
    // On initial load the page can be shorter than the viewport, which falsely triggers this.
    const nearBottom =
      window.scrollY > 0
      && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 80;

    if (nearBottom) {
      return SEP_SECTIONS[SEP_SECTIONS.length - 1].id;
    }

    if (window.scrollY < 10) {
      return SEP_SECTIONS[0].id;
    }

    let current = SEP_SECTIONS[0].id;
    for (const section of SEP_SECTIONS) {
      const el = document.getElementById(section.id);
      if (!el) continue;
      const top = el.getBoundingClientRect().top + window.scrollY;
      if (top <= scrollPosition) {
        current = section.id;
      }
    }
    return current;
  }, []);

  const scrollToSection = useCallback((id: SectionId) => {
    setActiveId(id);

    const el = document.getElementById(id);
    if (!el) return;
    const offset = measureStickyTopPx();
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }, []);

  useEffect(() => {
    let rafId = 0;

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setActiveId(resolveActiveSection());
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [resolveActiveSection, stickyTopPx]);

  const activeLabel = SEP_SECTIONS.find(s => s.id === activeId)?.label ?? '';

  const navStyle: React.CSSProperties = {
    top: stickyTopPx,
    maxHeight: `calc(100vh - ${stickyTopPx}px)`,
  };

  return (
    <>
      {/* ── Desktop sticky rail ── */}
      <aside className="hidden md:flex w-[240px] flex-shrink-0 self-stretch">
        <nav
          className="sticky z-20 flex w-full flex-col overflow-y-auto overscroll-contain bg-[#F5F7FA] py-1"
          style={navStyle}
          aria-label="Strategic Execution Plan section index"
        >
          <p className="text-[10px] font-semibold text-[#18365E]/40 uppercase tracking-widest mb-3 px-1">
            Report Index
          </p>

          <ol className="flex flex-col gap-0.5">
            {SEP_SECTIONS.map((section, idx) => {
              const isActive = activeId === section.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all group ${
                      isActive
                        ? 'bg-[#18365E]/[0.07] text-[#18365E] font-semibold'
                        : 'text-[#717182] hover:text-[#18365E] hover:bg-[#18365E]/[0.04] font-medium'
                    }`}
                  >
                    <span
                      className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                        isActive
                          ? 'bg-[#18365E] text-white'
                          : 'bg-[#18365E]/10 text-[#18365E]/50 group-hover:bg-[#18365E]/20 group-hover:text-[#18365E]/70'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <span className="leading-snug">{section.label}</span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="mt-5 pt-4 border-t border-[#e5e7eb]">
            <p className="text-[9px] text-[#717182]/60 px-1 leading-relaxed">
              Strategic Execution Plan
              <br />
              <span className="text-[#FF6E4C]/70">DVA · Adaptive Product</span>
            </p>
          </div>
        </nav>
      </aside>

      {/* ── Mobile dropdown ── */}
      <div className="md:hidden mb-3">
        <button
          type="button"
          onClick={() => setMobileOpen(v => !v)}
          className="flex items-center justify-between w-full px-3 py-2 bg-white border border-[#e5e7eb] rounded-lg text-xs font-semibold text-[#18365E] shadow-sm"
        >
          <span>Jump to: {activeLabel}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-[#18365E]/50 transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileOpen && (
          <div className="mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-md overflow-hidden z-30">
            {SEP_SECTIONS.map((section, idx) => {
              const isActive = activeId === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => { scrollToSection(section.id); setMobileOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 border-b border-[#e5e7eb] last:border-0 ${
                    isActive
                      ? 'bg-[#18365E]/[0.07] text-[#18365E] font-semibold'
                      : 'text-[#18365E] hover:bg-[#18365E]/[0.05]'
                  }`}
                >
                  <span className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
                    isActive ? 'bg-[#18365E] text-white' : 'bg-[#18365E]/10 text-[#18365E]/60'
                  }`}>
                    {idx + 1}
                  </span>
                  {section.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default SEPReportIndex;
